import forgotPasswordImageUrl from "@/assets/forgot-password.svg"
import { Button } from "@/components/chadcn/Button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/chadcn/Dialog"
import { Label } from "@/components/chadcn/Label"
import CopyRightsFooter from "@/components/CopyRightsFooter"
import HeroImage from "@/components/HeroImage"
import { PasswordInput } from "@/components/InputFields"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useCheckResetPasswordTokenQuery, useResetPasswordMutation } from "@/data/queries"
import { backendErrorCodeFor } from "@/model/BackendErrors"
import { ROUTES } from "@/routes"
import { resetPasswordSchema, type ResetPasswordFormData } from "@/schemas/auth"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useSearchParams } from "react-router"
import { toast } from "sonner"

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get("token") ?? ""
  const userId = searchParams.get("entityId") ?? ""

  const [hasSubmitted, setHasSubmitted] = useState(false)

  useEffect(() => {
    if (!token || !userId) {
      navigate(ROUTES.SIGN_IN, { replace: true })
    }
  }, [token, userId, navigate])

  const params = token && userId ? { token, userId } : null

  const { isPending: isCheckingToken, error: checkTokenError } =
    useCheckResetPasswordTokenQuery(params)

  const {
    mutateAsync: resetPassword,
    isPending: isResetting,
    error: resetError,
    isSuccess: resetSucceeded,
  } = useResetPasswordMutation()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    mode: "all",
    defaultValues: { password: "", passwordConfirmation: "" },
    resolver: zodResolver(resetPasswordSchema),
  })

  const checkErrorCode = backendErrorCodeFor(checkTokenError)
  const hasValidToken = !isCheckingToken && checkTokenError === null

  const tokenErrorMessage =
    checkErrorCode === "RESET_PASSWORD_TOKEN_EXPIRED"
      ? "Your forgot password link has been expired! Please click the button below to resend forgot password email."
      : checkErrorCode === "RESET_PASSWORD_ALREADY_EXECUTED"
        ? "This forgot password link has already been used! Please click the button below to go back and login"
        : null

  const resetErrorCode = backendErrorCodeFor(resetError)
  const resetErrorMessage =
    resetErrorCode === "WEAK_PASSWORD"
      ? "The password is commonly used and easily guessed. Please choose a stronger password"
      : resetErrorCode
        ? "Something went wrong. Please try again."
        : null

  const onSubmit = async (formData: ResetPasswordFormData) => {
    try {
      await resetPassword({ token, userId, password: formData.password })
      setHasSubmitted(true)
    } catch {
      // captured by useMutation; surfaced via resetError
    }
  }

  useEffect(() => {
    if (resetSucceeded && hasSubmitted) {
      toast.success("You've successfully updated your password!")
      navigate(ROUTES.SIGN_IN, { replace: true })
    }
  }, [resetSucceeded, hasSubmitted, navigate])

  if (!token || !userId) {
    return null
  }

  if (isCheckingToken) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12" aria-label="Checking reset link">
          <Spinner className="h-8 w-8" />
        </div>
      </Page>
    )
  }

  return (
    <Page>
      <HeroImage src={forgotPasswordImageUrl} alt="forgot-password" />
      <h2 className="mb-8 px-6 text-center text-h2 font-bold">Create new password</h2>

      {hasValidToken && (
        <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-1">
            <Label htmlFor="password">Password</Label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              disabled={isResetting}
              {...register("password")}
              placeholder="password"
              isError={!!errors.password?.message}
            />
            {errors.password?.message && (
              <p className="text-sm text-error">{errors.password.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="password-confirmation">Confirm password</Label>
            <PasswordInput
              id="password-confirmation"
              autoComplete="new-password"
              disabled={isResetting}
              {...register("passwordConfirmation")}
              placeholder="Confirm password"
              isError={!!errors.passwordConfirmation?.message}
            />
            {errors.passwordConfirmation?.message && (
              <p className="text-sm text-error">{errors.passwordConfirmation.message}</p>
            )}
          </div>

          {resetErrorMessage && (
            <div className="rounded-md border border-error/30 bg-error/5 p-3">
              <p className="text-sm text-error">{resetErrorMessage}</p>
            </div>
          )}

          <Button type="submit" disabled={isResetting} className="flex w-full gap-3 py-2 text-base">
            {isResetting && <Spinner />}
            Reset password
          </Button>
        </form>
      )}

      <CopyRightsFooter />

      <Dialog open={tokenErrorMessage !== null}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>An error occurred</DialogTitle>
          </DialogHeader>
          <p className="mt-2 text-sm">{tokenErrorMessage}</p>
          <Button
            type="button"
            className="mt-4 w-full"
            onClick={() => {
              if (checkErrorCode === "RESET_PASSWORD_TOKEN_EXPIRED") {
                navigate(ROUTES.FORGOT_PASSWORD, { replace: true })
              } else {
                navigate(ROUTES.SIGN_IN, { replace: true })
              }
            }}
          >
            {checkErrorCode === "RESET_PASSWORD_TOKEN_EXPIRED"
              ? "Resend email"
              : "Go back and log in"}
          </Button>
        </DialogContent>
      </Dialog>
    </Page>
  )
}

export default ResetPasswordPage
